import { Icon, Ellipsis } from '@zoom/components';
import classnames from 'classnames';
import { TFunction } from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DropdownToggle, DropdownMenu, DropdownItem, UncontrolledDropdown } from 'reactstrap';

import { useAuth, Permissions } from '../../app/AuthorizationProvider';
import { useGetAvailableApplicationsQuery } from '../../graphql/components';
import { PATH_MANAGE_ACCOUNT } from '../../router/routes';
import CustomIcon from '../Icon';
import { appSwitcherLinks, userLinks, UserLinkType } from './config';

interface IconProps {
  color: string;
  height: number;
  width?: number;
}

interface DropdownItemTarget {
  nameKeySlug: string;
  testIdSlug: string;
}

const dropdownItemTargets: { [key: string]: DropdownItemTarget } = {
  appSwitcher: {
    nameKeySlug: 'appSwitcher',
    testIdSlug: 'app-switcher',
  },
  userMenu: {
    nameKeySlug: 'userMenu',
    testIdSlug: 'user-menu',
  },
};

const customDropdownItem = (
  { href, icon, nameKey, slug, target }: UserLinkType,
  t: TFunction,
  { nameKeySlug, testIdSlug }: DropdownItemTarget,
  { color, height, width }: IconProps
) => (
  <DropdownItem
    data-test-id={`navbar-footer:${testIdSlug}:link-to:${slug}`}
    to={href}
    key={nameKey}
    tag={Link}
    target={target}
  >
    <Icon kind={icon} width={width} height={height} color={color} />
    {t(`layout.navbar.footer.${nameKeySlug}.links.${nameKey}.title`)}
  </DropdownItem>
);

const UserMenuItems = (links: UserLinkType[], t: TFunction) =>
  links.map((linkProps: UserLinkType) =>
    customDropdownItem(linkProps, t, dropdownItemTargets.userMenu, { color: 'blue-light', height: 24, width: 24 })
  );

const AppSwitcherItems = (links: UserLinkType[], t: TFunction) =>
  links.map((linkProps: UserLinkType) =>
    customDropdownItem(linkProps, t, dropdownItemTargets.appSwitcher, { color: '#839094', height: 28 })
  );

export const DrawerFooter: React.FC = () => {
  const { userName, userHasPermissions } = useAuth();
  const { t } = useTranslation();

  const { data: availableApplicationsData } = useGetAvailableApplicationsQuery({
    skip: !userHasPermissions([Permissions.ViewAppSwitcher]),
  });

  const dropdownAppsLinks = useMemo(() => {
    const availableApplicationNames = availableApplicationsData?.availableApplications.map(
      ({ applicationName }) => applicationName
    );

    if (!availableApplicationNames?.length) {
      return [];
    }

    return appSwitcherLinks.filter(({ kcAppName }) => availableApplicationNames.includes(kcAppName));
  }, [availableApplicationsData]);

  const userMenuToggleHandler = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (!e.nativeEvent.altKey && !e.nativeEvent.ctrlKey && !e.nativeEvent.metaKey && !e.nativeEvent.shiftKey) {
      // skip direct routing to href onClick
      e.preventDefault();
    }
  };

  const hasSomeAppLinks = !!dropdownAppsLinks.length;

  const UserIcon = (
    <div className="icon-outer-layer">
      <Icon kind="user" color="grey1" size={20} />
    </div>
  );

  return (
    <div id="navbar-footer">
      <UncontrolledDropdown
        className={classnames('navbar-footer__user-menu', {
          'navbar-footer__user-menu--expanded': !hasSomeAppLinks,
        })}
        direction="up"
      >
        <DropdownToggle
          color="transparent"
          className="navbar-footer__user-menu__toggle disabled"
          data-test-id="navbar-footer:user-menu:toggle"
          to={PATH_MANAGE_ACCOUNT}
          onClick={userMenuToggleHandler}
          tag={Link}
        >
          <div className="navbar-footer__user-menu__icon-wrapper">{UserIcon}</div>
          {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
          <Ellipsis className="navbar-footer__username">{userName}</Ellipsis>
        </DropdownToggle>
        <DropdownMenu modifiers={{ offset: { enabled: true, offset: 22 } }}>
          <DropdownItem className="navbar-footer__user-menu__header" header tag="div">
            <div className="header-item__icon-wrapper">{UserIcon}</div>
            {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
            <Ellipsis className="header-item__username" data-test-id="navbar-footer:user-name">
              {userName}
            </Ellipsis>
          </DropdownItem>
          {UserMenuItems(userLinks, t)}
        </DropdownMenu>
      </UncontrolledDropdown>
      {hasSomeAppLinks && (
        <UncontrolledDropdown className="navbar-footer__app-switcher" direction="up">
          <DropdownToggle
            color="transparent"
            data-test-id="navbar-footer:app-switcher:toggle"
            id="navbar-footer-switch"
            tag="a"
          >
            <CustomIcon kind="app-switcher" color="#5c5e5e" size={26} id="zoom-app-switcher" />
          </DropdownToggle>
          <DropdownMenu modifiers={{ offset: { enabled: true, offset: 22 } }}>
            <DropdownItem className="navbar-footer__app-switcher__header" header>
              {t('layout.navbar.footer.appSwitcher.title')}
            </DropdownItem>
            {AppSwitcherItems(dropdownAppsLinks, t)}
          </DropdownMenu>
        </UncontrolledDropdown>
      )}
    </div>
  );
};
